import '../App.css';
import ProductList from '../components/ProductList';
import Header from '../components/Header';
import Search from '/asset/search.svg';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch } from '../store/store';
import { RootState } from '../store/store';
import {
  fetchCategories,
  fetchProducts,
  setSelectedProduct,
} from '../store/productSlice';
import Loading from '../components/Loading';
import { fetchPurchaseCart } from '../store/purchaseSlice';
import Variants from '../components/Variants';
import Banner from '../components/Banner.tsx';
import CategoriesList from '../components/CategoriesList';
import CartFooter from '../components/CartFooter.tsx';
import Content from '../components/Content.tsx';
import ProductDetails from '../components/ProductDetails.tsx';
import BNPLBar from '../components/BNPLBar.tsx';

function StoreFront() {
  const dispatch = useDispatch<AppDispatch>();
  const categories = useSelector(
    (state: RootState) => state.products.categories
  );

  const { variants, selectedProduct, showDetails, isLightboxOpen } =
    useSelector((state: RootState) => state.products);
  const { banner } = useSelector((state: RootState) => state.products);
  const { storeInfo } = useSelector((state: RootState) => state.products);
  const [keyword, setKeyWord] = useState<string | null>(null);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const products = useSelector((state: RootState) => state.products.products);
  const products_loading = useSelector(
    (state: RootState) => state.products.loading
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        setKeyWord(e.target.value);
      }, 1000);
    },
    []
  );

  const showFilters = true;
  // Fetch products on mount
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchProducts({
        filters: { categoryIds, keyword },
      })
    );
  }, [dispatch, categoryIds, keyword]);

  useEffect(() => {
    dispatch(fetchPurchaseCart());
  }, [dispatch]);
  const searchBarPlaceholder =
    storeInfo?.searchBarPlaceholder?.trim() || 'Search for product';
  return (
    <div
      className="w-full min-h-screen flex flex-col 
    relative bg-white pb-[100px]"
    >
      {showDetails && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 px-2 md:px-4"
          onClick={() => dispatch(setSelectedProduct(null))}
        >
          <div
            className="relative max-h-[80vh] 
            w-full max-w-[1200px] p-[10px]
            overflow-hidden rounded-2xl bg-white
            "
            role="dialog"
            aria-modal="true"
            aria-label={`${selectedProduct?.name} details`}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className=" 
              max-h-[90vh]
              md:max-h-[60vh] 
              lg:max-h-[70vh]
              w-full 
              p-4
              max-w-[1200px] 
              overflow-y-auto rounded-2xl bg-white
              thin-light-scrollbar"
            >
              {!isLightboxOpen && (
                <button
                  type="button"
                  onClick={() => dispatch(setSelectedProduct(null))}
                  className="absolute top-1.5 right-1.5 p-1 rounded-full
                  text-gray-300 hover:text-red-400 
                  bg-primary-50 
                  border-[2px]
                  border-solid
                  border-secondary-300
                  text-primary-500
                  hover:bg-red-50
                  z-[300]
                  transition-colors"
                  aria-label="Close product details"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
              <ProductDetails productId={String(selectedProduct?.id)} />
            </div>
          </div>
        </div>
      )}

      <div>
        <BNPLBar />
        <Header logoOnly={false} />
        {banner && <Banner url={banner} />}
        {variants && <Variants />}
      </div>
      <div
        className="flex flex-col lg:flex-row w-full 
      relative pb-[200px]"
      >
        <div className="flex-1 w-full">
          <div
            className="sticky top-0 z-[50] 
          w-full bg-secondary-50 border-b 
          border-gray-100 shadow-sm"
          >
            <div className="m-auto flex content-center px-[10px] py-[10px]">
              <Content>
                <div
                  className="w-full max-w-[1250px] bg-white 
                  border border-[2px] border-secondary-50
                  hover:border-secondary-300 
                  content-center m-auto mt-[3px] lg:flex 
                  rounded-md px-[10px] flex gap-[10px]"
                >
                  <img
                    src={Search}
                    alt="Search"
                    className="w-[20px] h-[20px] mt-[10px] 
                    object-contain object-center"
                  />
                  <input
                    data-cy="product-search-input"
                    data-name="searchText"
                    defaultValue={keyword ? keyword : ''}
                    className="w-full h-[40px] pr-[10px] 
                    bg-transparent  font-dmsans 
                    outline-none text-[13px]"
                    placeholder={searchBarPlaceholder}
                    onChange={handleSearchChange}
                  ></input>
                </div>
              </Content>
            </div>
          </div>

          <div className="w-full relative">
            <div className="">
              {!products_loading && (
                <div className="bg-white ">
                  <CategoriesList
                    sortedCategories={categories}
                    categoryIds={categoryIds}
                    setCategoryIds={setCategoryIds}
                    showFilters={showFilters}
                  />
                </div>
              )}

              <div className="mx-[30px] min-h-[300px] mt-[30px]">
                {products_loading && <Loading text="Loading items"></Loading>}

                {!products_loading && (
                  <ProductList
                    products={products}
                    keyword={keyword}
                    categoryIds={categoryIds}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <CartFooter />
    </div>
  );
}

export default StoreFront;
